import { type CSSProperties, type ReactElement, useId, useMemo } from "react";

import type { IDropdownProps } from "@similarweb/ui-components";
import { Dropdown, DropdownButton, DropdownItem } from "@similarweb/ui-components";

import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { DROPDOWN_HEIGHT, DROPDOWN_WIDTH, StyledDropdownParent } from "./styles";
import type { DropdownValue, FormSelectValue } from "./types";

const SELECT_ID = "form-select";

const defaultFormatter = <V extends FormSelectValue>(value?: V): string => `${value}`;

export interface IFormSelectBaseProps<V extends FormSelectValue> extends Partial<IDropdownProps<V>> {
    fieldName: string;
    dropdownPopupPlacement?: "ontop-left" | "bottom-right" | "bottom-left" | "top" | "bottom" | "left" | "right";
    formatValue?: (value: V) => string;
}

export interface IFormSelectProps<V extends FormSelectValue> extends IFormSelectBaseProps<V> {
    value?: V | readonly V[];
    options: readonly V[];
    placeholder?: string;
    width?: number;
    buttonWidth?: number;
    isDisplayValue?: boolean;
    dropdownWrapperStyled?: CSSProperties;
    renderButton?: () => ReactElement;
    renderItem?: (option: V) => ReactElement;
    onChange: (value: V) => void;
    onToggle?: (isOpen: boolean, isOutsideClick: boolean) => void;
    isDisabled?: boolean;
}

/**
 * @deprecated Legacy eager-rendering select built on the old `Dropdown` + `appendTo` pattern (its
 * whole option list renders even while closed). For single-select value lists use
 * `FormSelectCombobox` (`common-components/dropdown/FormSelectCombobox`), whose options mount only
 * while open. Still used for the multi-select case via `MultiSelectAutocomplete`, and by a few
 * non-filter forms (webhook table cell, AI-agent meeting goal).
 */
export const FormSelect = <V extends FormSelectValue>({
    fieldName,
    value,
    options,
    placeholder = "",
    dropdownWrapperStyled,
    dropdownPopupPlacement = "ontop-left",
    formatValue = defaultFormatter<V>,
    onChange,
    onToggle,
    renderButton,
    renderItem,
    width = DROPDOWN_WIDTH,
    buttonWidth,
    isDisplayValue = true,
    isDisabled = false,
    ...dropdownProps
}: IFormSelectProps<V>): ReactElement => {
    const dropdownId = useId();

    const arrayValue = is.undefined(value) ? [] : Array.isArray(value) ? value : [value];
    const hasValue = arrayValue.length > 0;

    const displayedValue = useMemo(() => {
        if (!hasValue || !isDisplayValue) {
            return placeholder;
        }

        return arrayValue.map(formatValue).join(", ");
    }, [value, placeholder, isDisplayValue]);

    const selectedIds = useMemo(
        () =>
            arrayValue.reduce(
                (acum, valueItem) => ({
                    ...acum,
                    [valueItem]: true,
                }),
                {},
            ),
        [value],
    );

    const handleClick = ({ id }: DropdownValue<V>): void => {
        onChange(id);
    };

    const renderDropdownButton = () => {
        if (renderButton) {
            return renderButton();
        }

        return <DropdownButton disabled={isDisabled}>{displayedValue}</DropdownButton>;
    };

    const renderDropdownItem = (option: V): ReactElement => {
        if (renderItem) {
            return renderItem(option);
        }

        return (
            <DropdownItem
                key={option}
                id={option}
            >
                {formatValue(option)}
            </DropdownItem>
        );
    };

    return (
        <StyledDropdownParent
            width={buttonWidth || width}
            style={dropdownWrapperStyled}
            id={dropdownId}
            data-automation-id={`${SELECT_ID}-${fieldName}`}
        >
            <Dropdown
                appendTo={`#${CSS.escape(dropdownId)}`}
                selectedIds={selectedIds}
                width={width}
                dropdownPopupHeight={DROPDOWN_HEIGHT}
                dropdownPopupPlacement={dropdownPopupPlacement}
                onClick={handleClick}
                onToggle={onToggle}
                buttonWidth={buttonWidth}
                {...dropdownProps}
            >
                {renderDropdownButton()}

                {...options.map(renderDropdownItem)}
            </Dropdown>
        </StyledDropdownParent>
    );
};
